import { mdiDeleteOutline, mdiEyeOutline, mdiTrayArrowDown } from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";
import { useState } from "react";
import { useToggle } from "usehooks-ts";

import ConfirmationModal from "components/ConfirmationModal";
import Select from "components/Select";
import { VISIBILITY_LIST } from "constants/haulpay-broker";
import { IOption } from "models/Form";
import { IFRAttachment } from "models/HaulPay";
import styles from "./style.module.scss";
import { updateAttachment } from "services/api/haulpay";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";

type FRAttachmentItemProps = {
  attachment: IFRAttachment;
  fundingRequestId?: string;
  showVisibleTo?: boolean;
  showPreview?: boolean;
  showDownload?: boolean;
  showDelete?: boolean;
  onDelete?: (file_id: string) => void;
};

export default function FRAttachmentItem({
  attachment,
  fundingRequestId,
  showVisibleTo = false,
  showPreview = true,
  showDownload = true,
  showDelete = false,
  onDelete
}: Readonly<FRAttachmentItemProps>) {
  const [visibleTo, setVisibleTo] = useState<IOption>(
    VISIBILITY_LIST.find((visibleTo) => visibleTo.value === attachment.visibleTo.join(",")) ??
      VISIBILITY_LIST[0]
  );
  const [showDeleteConfirmationModal, toggleDeleteConfirmationModal] = useToggle();

  const downloadFile = (url: string) => {
    window.open(url, "_blank");
  };

  const previewFile = (url: string) => {
    const newWindow = window.open(url, "_blank");
    if (newWindow) {
      newWindow.focus();
    }
  };

  const handleUpdateVisibilityTo = async (visibility: IOption) => {
    if (!fundingRequestId) return;
    try {
      setVisibleTo(visibility);
      await updateAttachment<IFRAttachment>({
        attachment: {
          ...attachment,
          category:
            typeof attachment.category === "string"
              ? attachment.category
              : attachment.category.join(","),
          visible_to: visibility.value.split(",")
        },
        fundingRequestId,
        attachmentId: attachment.id
      });
      showSuccessMessage("Visibility has been updated.");
    } catch (e) {
      showErrorMessage("Error while updating attachment.");
    }
  };

  return (
    <div className={styles.frAttachment}>
      <div className={styles.fileData}>
        <div className="flex-1 desktop:flex-none">
          <span className="text-placeholder">File Name</span>
          <span className="underline text-info">
            {attachment.filename}
            <br />
            {dayjs(attachment.uploaded).format("M/DD/YYYY hh:mm:ss A")}
          </span>
        </div>
        <div className="flex-1">
          <span className="text-placeholder">Category</span>
          <span>
            {typeof attachment.category === "string"
              ? attachment.category
              : attachment.category.join(",")}
          </span>
        </div>
      </div>

      <div className="desktop:flex-none flex gap-2 justify-end">
        {showVisibleTo && (
          <div className="flex items-center gap-2 flex-1">
            <Select
              label="Visibility"
              options={VISIBILITY_LIST}
              value={visibleTo}
              onChange={(visibility) =>
                fundingRequestId ? handleUpdateVisibilityTo(visibility) : setVisibleTo(visibility)
              }
              className="flex-1"
              btnClass="mobile:!p-1.5 !p-2 mobile:!pr-10 !pr-14 !rounded !bg-transparent !border-tertiary mobile:!w-24 mobile:!text-xs"
            />
          </div>
        )}

        {showPreview && attachment.preview_url && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => previewFile(attachment.preview_url ?? "")}>
            <Icon path={mdiEyeOutline} />
          </button>
        )}

        {showDownload && attachment.download_url && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => downloadFile(attachment.download_url ?? "")}>
            <Icon path={mdiTrayArrowDown} />
          </button>
        )}

        {showDelete && attachment.download_url && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={toggleDeleteConfirmationModal}>
            <Icon path={mdiDeleteOutline} />
          </button>
        )}
      </div>

      <ConfirmationModal
        content="Are you sure you want to delete this attachment?"
        isConfirmModalOpen={showDeleteConfirmationModal}
        toggleConfirmModal={toggleDeleteConfirmationModal}
        onOK={onDelete ? () => onDelete(attachment.id) : () => {}}
      />
    </div>
  );
}
